import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initializeFirebase } from '@/lib/firebase';
import { ErrorBoundary } from '@/components/error/ErrorBoundary';
import { App } from './App';
import './index.css';

// Initialize Firebase before rendering the app
initializeFirebase();

/**
 * Prevent page-level pinch zoom on mobile browsers.
 * This is needed because iOS Safari ignores viewport meta tag settings.
 * The map component handles its own pinch-to-zoom via Konva.
 *
 * We allow zoom gestures on canvas elements (for map) but block them elsewhere.
 */
const preventPageZoom = () => {
  /**
   * Check if the touch target is inside a canvas (map component)
   * If so, let the map handle the gesture instead of blocking it.
   */
  const isInsideCanvas = (target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) return false;
    return target.tagName === 'CANVAS' || target.closest('canvas') !== null;
  };

  // Prevent multi-touch gestures (pinch zoom) at document level
  // but allow them inside canvas elements (map)
  document.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      if (e.touches.length > 1 && !isInsideCanvas(e.target)) {
        e.preventDefault();
      }
    },
    { passive: false }
  );

  // Prevent iOS gesture events (pinch/rotate) outside of canvas
  document.addEventListener(
    'gesturestart',
    (e: Event) => {
      if (!isInsideCanvas(e.target)) {
        e.preventDefault();
      }
    },
    { passive: false }
  );

  document.addEventListener(
    'gesturechange',
    (e: Event) => {
      if (!isInsideCanvas(e.target)) {
        e.preventDefault();
      }
    },
    { passive: false }
  );

  document.addEventListener(
    'gestureend',
    (e: Event) => {
      if (!isInsideCanvas(e.target)) {
        e.preventDefault();
      }
    },
    { passive: false }
  );
};

preventPageZoom();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

/**
 * Log errors to console and potentially to error tracking service
 */
const handleError = (error: Error) => {
  console.error('Application error:', error);
  // TODO: Send to error tracking service (e.g., Sentry)
};

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary onError={handleError}>
      <App />
    </ErrorBoundary>
  </StrictMode>
);
